import { PropsWithChildren } from 'react';
import { isRoleAllowed } from '@magicbrief/server/src/permissions/utils/is-role-allowed';
import classNames from 'classnames';
import TikTokLogoWhite from 'src/assets/logos/TikTokLogoWhite';
import { useI18nContext } from 'src/i18n/i18n-react';
import useFeatureFlag from 'src/utils/useFeatureFlag';
import { usePopupWindow } from 'src/utils/usePopupWindow';
import { useUserAndOrganisation } from 'src/utils/useUserAndOrganisation';
import { AriaButton, ButtonVariants } from '../Button/Button';

type Props = {
  variant?: ButtonVariants;
  icon?: React.ReactNode | null;
  className?: string;
  additionalOnPress?: () => void;
  onPopupClose: () => void;
};

export const TikTokConnect: React.FC<PropsWithChildren<Props>> = ({
  variant = 'primary',
  icon = <TikTokLogoWhite />,
  className,
  children,
  additionalOnPress,
  onPopupClose,
}) => {
  const { LL } = useI18nContext();
  const user = useUserAndOrganisation();
  const oauthUrl = useFeatureFlag('TIK_TOK_OAUTH_URL');
  const { openPopupWindow } = usePopupWindow();

  if (!oauthUrl || !user.data) {
    return null;
  }

  const isUserAllowed = user.data.role
    ? isRoleAllowed(user.data.role, 'editor')
    : false;

  return (
    <AriaButton
      className={classNames(className)}
      variant={variant}
      isDisabled={!isUserAllowed}
      onPress={() => {
        additionalOnPress?.();
        openPopupWindow({
          url: new URL(oauthUrl),
          onClose: onPopupClose,
        });
      }}
      icon={icon}
    >
      {children
        ? children
        : user.data.organisation.hasTikTokIntegrations
          ? LL.integrations.tikTok.connectTikTok.ctaModify()
          : LL.integrations.tikTok.connectTikTok.ctaConnect()}
    </AriaButton>
  );
};
